import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as couponsApi from '../api/couponsApi';
import { Card, ConfirmDialog, EmptyState, ErrorBanner, Modal, Spinner, TableSkeleton } from '../components/ui';
import { IconEye, IconPencil, IconPlus, IconTrash } from '../components/icons';
import { Coupon, CouponType } from '../types/coupon';
import { formatDate, formatINR } from '../utils/format';

const EMPTY_FORM = {
  code: '',
  description: '',
  type: 'percentage' as CouponType,
  value: '',
  max_discount_value: '',
  min_order_value: '',
  max_uses: '',
  expires_at: '',
  active: true,
  // Private by default — a new code is not advertised until someone says so.
  visible: false,
  in_banner: false,
};

type CouponForm = typeof EMPTY_FORM;

/**
 * Load an existing coupon into the form.
 *
 * Every field is stated — a blank left here reads to the admin as "this coupon
 * has no cap / no limit / no expiry" and would be saved back as exactly that,
 * so a missed field is a silent data change rather than a cosmetic gap.
 *
 * Numbers become strings because the inputs are controlled: `value={0}` and
 * `value={null}` render very differently, and the empty string is what the rest
 * of this form already means by "not set".
 */
function formFromCoupon(c: Coupon): CouponForm {
  return {
    code: c.code,
    description: c.description ?? '',
    type: c.type,
    value: String(c.value ?? ''),
    max_discount_value: c.max_discount_value == null ? '' : String(c.max_discount_value),
    // 0 is the API's default minimum, and showing it beats an empty box that
    // looks unset; it round-trips back as 0 either way.
    min_order_value: c.min_order_value == null ? '' : String(c.min_order_value),
    max_uses: c.max_uses == null ? '' : String(c.max_uses),
    // <input type="date"> only accepts YYYY-MM-DD; stored values may carry a
    // time component, which would blank the field outright.
    expires_at: c.expires_at ? c.expires_at.slice(0, 10) : '',
    active: c.active,
    visible: c.visible,
    in_banner: c.in_banner,
  };
}

/**
 * The switch used for every on/off field on this page.
 *
 * `disabled` is a real state here, not decoration: "show in banner" is
 * meaningless on a coupon customers can't see, so it greys out rather than
 * letting an admin set a combination the API would reject.
 */
function Toggle({
  label, description, checked, onChange, disabled = false,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-50' : ''}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {description && <p className="mt-0.5 text-xs text-sub">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-gold' : 'bg-edge'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

export default function CouponsPage() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const [actionSuccess, setActionSuccess] = useState('');

  // One modal serves both jobs. `editTarget` is the coupon being edited — null
  // while creating — and it holds the ORIGINAL row, which is what addresses the
  // record on save and what tells us whether the code may still be renamed.
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Coupon | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [togglingCode, setTogglingCode] = useState('');
  const [visibilityCode, setVisibilityCode] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Coupon | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCoupons(await couponsApi.getCoupons());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setActionError('');
    setActionSuccess('');
    setShowForm(true);
  };

  const openEdit = (coupon: Coupon) => {
    setEditTarget(coupon);
    setForm(formFromCoupon(coupon));
    setActionError('');
    setActionSuccess('');
    setShowForm(true);
  };

  // Both paths send the same payload; only the verb differs. Blank optional
  // fields become explicit nulls so an edit can genuinely clear a cap, a usage
  // limit or an expiry date rather than being read as "no change".
  const handleSave = async () => {
    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      type: form.type,
      value: Number(form.value),
      max_discount_value:
        form.type === 'percentage' && form.max_discount_value
          ? Number(form.max_discount_value)
          : null,
      min_order_value: Number(form.min_order_value) || 0,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      expires_at: form.expires_at || null,
      active: form.active,
      visible: form.visible,
      in_banner: form.in_banner,
    };

    setSaving(true);
    setActionError('');
    setActionSuccess('');
    try {
      if (editTarget) {
        await couponsApi.updateCoupon(editTarget.code, payload);
      } else {
        await couponsApi.createCoupon(payload);
      }
      const savedCode = payload.code;
      const wasEdit = editTarget !== null;
      setShowForm(false);
      setEditTarget(null);
      setForm(EMPTY_FORM);
      // Reload before announcing, so the row the admin looks at next is the
      // saved one and not the stale copy the message is claiming was updated.
      await load();
      setActionSuccess(
        wasEdit ? 'Coupon updated successfully' : `Coupon ${savedCode} created successfully`,
      );
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (coupon: Coupon) => {
    setTogglingCode(coupon.code);
    setActionError('');
    setActionSuccess('');
    try {
      await couponsApi.toggleCoupon(coupon.code, !coupon.active);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setTogglingCode('');
    }
  };

  // Visibility is also editable straight from the row — the two flags are the
  // ones flipped most often, and opening the full form for a checkbox is noise.
  const handleVisibility = async (coupon: Coupon, next: { visible?: boolean; in_banner?: boolean }) => {
    setVisibilityCode(coupon.code);
    setActionError('');
    setActionSuccess('');
    try {
      await couponsApi.setCouponVisibility(
        coupon.code,
        next.visible ?? coupon.visible,
        next.in_banner ?? coupon.in_banner,
      );
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setVisibilityCode('');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError('');
    setActionSuccess('');
    try {
      await couponsApi.deleteCoupon(deleteTarget.code);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // A redeemed coupon's code is frozen: orders store the code, and that history
  // lives in the order service's own database, so a rename here could never be
  // cascaded to it. The API refuses it too — this only keeps the field honest.
  const codeLocked = editTarget !== null && editTarget.used_count > 0;

  const formValid =
    form.code.trim() &&
    Number(form.value) > 0 &&
    (form.type !== 'percentage' || Number(form.value) <= 100) &&
    // Blank means uncapped; anything typed must be a positive amount.
    (!form.max_discount_value || Number(form.max_discount_value) > 0) &&
    // Blank means unlimited; anything typed must be at least one use.
    (!form.max_uses || Number(form.max_uses) > 0) &&
    Number(form.min_order_value || 0) >= 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Coupons</h1>
          <p className="text-sm text-sub">Discount codes and promotions</p>
        </div>
        <button className="btn-gold" onClick={openCreate}>
          <IconPlus className="h-4 w-4" />
          Create coupon
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {actionError && <ErrorBanner message={actionError} />}
      {actionSuccess && (
        <div className="rounded-xl border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-600 dark:text-green-300">
          {actionSuccess}
        </div>
      )}

      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={6} />
        ) : coupons.length === 0 ? (
          <EmptyState message="No coupons yet" hint="Create your first coupon to get started" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Code</th>
                  <th className="th">Type</th>
                  <th className="th">Value</th>
                  <th className="th">Min order</th>
                  <th className="th">Used / Max</th>
                  <th className="th">Expires</th>
                  <th className="th">Status</th>
                  <th className="th">Shown to customers</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {coupons.map((c) => {
                  const expired = c.expires_at ? new Date(c.expires_at).getTime() < Date.now() : false;
                  return (
                    <tr key={c.code} className="transition-colors hover:bg-edge/20">
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold tracking-wide">{c.code}</p>
                          {/* The eye marks a code customers can actually see
                              listed — the rest work only if they are typed in. */}
                          {c.visible && (
                            <span
                              title="Listed to customers at checkout"
                              className="inline-flex items-center gap-1 rounded-full border border-[#c9a96e]/40 bg-[#c9a96e]/10 px-2 py-0.5 text-[10px] font-medium text-[#a4834a] dark:text-[#c9a96e]"
                            >
                              <IconEye className="h-3 w-3" />
                              Public
                            </span>
                          )}
                        </div>
                        {c.description && <p className="text-xs text-sub">{c.description}</p>}
                      </td>
                      <td className="td text-sub">{c.type === 'percentage' ? 'Percentage' : 'Fixed'}</td>
                      <td className="td font-medium">
                        {c.type === 'percentage' ? `${c.value}%` : formatINR(c.value)}
                        {c.type === 'percentage' && c.max_discount_value != null && (
                          <p className="text-xs font-normal text-sub">
                            max {formatINR(c.max_discount_value)}
                          </p>
                        )}
                      </td>
                      <td className="td text-sub">{formatINR(c.min_order_value)}</td>
                      <td className="td text-sub">
                        {c.used_count ?? 0} / {c.max_uses ?? '∞'}
                      </td>
                      <td className={`td ${expired ? 'text-bad' : 'text-sub'}`}>
                        {c.expires_at ? formatDate(c.expires_at) : 'Never'}
                      </td>
                      <td className="td">
                        {c.active && !expired ? (
                          <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full border border-slate-500/30 bg-slate-500/10 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-400/30 dark:bg-slate-400/10 dark:text-slate-300">
                            {expired ? 'Expired' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td className="td">
                        <div className="flex flex-col gap-1.5">
                          <label className="flex cursor-pointer items-center gap-2 text-xs text-sub">
                            <input
                              type="checkbox"
                              className="accent-[#c9a96e]"
                              checked={c.visible}
                              disabled={visibilityCode === c.code}
                              onChange={() => handleVisibility(c, { visible: !c.visible })}
                            />
                            At checkout
                          </label>
                          {/* Greyed out on a private coupon: broadcasting a code
                              customers can't even see listed is a contradiction,
                              and the API rejects that pairing outright. */}
                          <label
                            className={`flex items-center gap-2 text-xs text-sub ${
                              c.visible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="accent-[#c9a96e]"
                              checked={c.in_banner}
                              disabled={!c.visible || visibilityCode === c.code}
                              onChange={() => handleVisibility(c, { in_banner: !c.in_banner })}
                            />
                            In banner
                          </label>
                        </div>
                      </td>
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <button
                            className="btn-outline !px-3 !py-1.5 !text-xs"
                            disabled={togglingCode === c.code}
                            onClick={() => handleToggle(c)}
                          >
                            {togglingCode === c.code && <Spinner className="h-3 w-3" />}
                            {c.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn-ghost !p-1.5"
                            aria-label={`Edit ${c.code}`}
                            onClick={() => openEdit(c)}
                          >
                            <IconPencil className="h-4 w-4" />
                          </button>
                          <button
                            className="btn-ghost !p-1.5 hover:!text-bad"
                            aria-label={`Delete ${c.code}`}
                            onClick={() => setDeleteTarget(c)}
                          >
                            <IconTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create / edit modal — same fields either way, so an admin never has to
          learn two forms and no field can drift between the two paths. */}
      <Modal
        open={showForm}
        title={editTarget ? `Edit coupon — ${editTarget.code}` : 'Create coupon'}
        onClose={() => setShowForm(false)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setShowForm(false)} disabled={saving}>
              Cancel
            </button>
            <button className="btn-gold" onClick={handleSave} disabled={saving || !formValid}>
              {saving && <Spinner />}
              {editTarget ? 'Save changes' : 'Create'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Code</label>
            <input
              className={`input uppercase ${codeLocked ? 'cursor-not-allowed opacity-60' : ''}`}
              placeholder="WELCOME10"
              value={form.code}
              readOnly={codeLocked}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
            {codeLocked && (
              <p className="mt-1 text-xs text-sub">Code cannot be changed after use</p>
            )}
          </div>
          <div>
            <label className="label">Description</label>
            <input
              className="input"
              placeholder="10% off for new customers"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <span className="label">Type</span>
            <div className="flex gap-4">
              {(['percentage', 'fixed'] as const).map((t) => (
                <label key={t} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="coupon-type"
                    className="accent-[#c9a96e]"
                    checked={form.type === t}
                    // A cap only applies to percentage coupons — drop any value
                    // already typed when switching to a fixed amount.
                    onChange={() =>
                      setForm({
                        ...form,
                        type: t,
                        max_discount_value: t === 'percentage' ? form.max_discount_value : '',
                      })
                    }
                  />
                  {t === 'percentage' ? 'Percentage (%)' : 'Fixed amount (₹)'}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">{form.type === 'percentage' ? 'Value (%)' : 'Value (₹)'}</label>
              <input
                className="input"
                type="number"
                min={1}
                max={form.type === 'percentage' ? 100 : undefined}
                placeholder={form.type === 'percentage' ? '10' : '200'}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
              />
            </div>
            {/* Caps the rupee discount a percentage can produce on a large cart.
                Meaningless for a fixed amount, so it's hidden in that mode. */}
            {form.type === 'percentage' && (
              <div>
                <label className="label">Max discount (₹)</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  placeholder="No limit"
                  value={form.max_discount_value}
                  onChange={(e) => setForm({ ...form, max_discount_value: e.target.value })}
                />
                <p className="mt-1 text-xs text-sub">
                  {form.value && form.max_discount_value
                    ? `${form.value}% off, capped at ${formatINR(Number(form.max_discount_value))}`
                    : 'Leave blank for an uncapped discount'}
                </p>
              </div>
            )}
            <div>
              <label className="label">Min order (₹)</label>
              <input
                className="input"
                type="number"
                min={0}
                placeholder="0"
                value={form.min_order_value}
                onChange={(e) => setForm({ ...form, min_order_value: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Max uses</label>
              <input
                className="input"
                type="number"
                min={1}
                placeholder="Unlimited"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Expiry date</label>
              <input
                className="input"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-4 border-t border-edge pt-4">
            <Toggle
              label="Active"
              checked={form.active}
              onChange={() => setForm({ ...form, active: !form.active })}
            />
            <Toggle
              label="Show to customers at checkout"
              description="Customers will see this coupon listed on the checkout page. Leave off for influencer or private codes — they still work when typed in."
              checked={form.visible}
              onChange={() =>
                setForm({
                  ...form,
                  visible: !form.visible,
                  // Going private takes the coupon out of the banner with it,
                  // rather than leaving a setting the API would reject on save.
                  in_banner: form.visible ? false : form.in_banner,
                })
              }
            />
            <Toggle
              label="Show in announcement banner"
              description="This offer scrolls in the top announcement strip on the landing page."
              checked={form.in_banner}
              disabled={!form.visible}
              onChange={() => setForm({ ...form, in_banner: !form.in_banner })}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete coupon?"
        message={
          <>
            Coupon <span className="font-semibold text-ink">{deleteTarget?.code}</span> will be
            permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        danger
        busy={deleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
